"use client";

import React, { useState, useEffect } from "react";
import Image from "next/image";
import Link from "next/link";
import { useRouter, useSearchParams } from "next/navigation";
import { motion } from "framer-motion";
import { Eye, EyeOff, Loader2 } from "lucide-react";
import { AnimatePresence } from "motion/react";
import OTPInput from "@/components/ui/OTPInput";
import svgPaths from "@/assets/svg/svg-mgmbampcwd";
import LogoIcon from "@/assets/images/logo.svg";
import styles from "@/app/login/page.module.css";
import { toast } from "sonner";

function Logo() {
	return (
		<div className="relative shrink-0 size-[2rem]" data-name="Logo">
			<Image
				src={LogoIcon}
				alt="Tax Lien Vest Logo"
				width={32}
				height={32}
				className="w-[2rem] h-[2rem]"
			/>
		</div>
	);
}

// Unused components - commented out for future use
// function TopBar() {
// 	return (
// 		<div
// 			className="content-stretch flex gap-[0.5rem] items-center relative shrink-0"
// 			data-name="Top bar"
// 		>
// 			<LogoContainer />
// 		</div>
// 	);
// }

// function BackgroundOverlay() {
// 	return (
// 		<div
// 			className="relative bg-[#142a1e] w-full h-full overflow-hidden flex items-end justify-end"
// 			data-name="Background overlay"
// 		>
// 			<div
// 				className="relative w-[31.25rem] overflow-hidden flex-shrink-0"
// 				data-name="Hero Image Right"
// 				style={{
// 					height: "calc(300px + 4rem)",
// 					marginBottom: "-4rem",
// 				}}
// 			>
// 				<div className="relative w-full h-full">
// 					<Image
// 						alt=""
// 						src={imgHeroImageRight}
// 						fill
// 						className="max-w-none object-right-bottom object-cover pointer-events-none"
// 						sizes="31.25rem"
// 						style={{
// 							objectPosition: "right bottom",
// 							display: "block",
// 						}}
// 					/>
// 				</div>
// 			</div>
// 		</div>
// 	);
// }

function SearchIconContainer() {
	return (
		<Link
			href="/"
			className="size-[3rem] cursor-pointer hover:opacity-70 transition-opacity"
			data-name="Search Icon Container"
			data-testid="login-back-button"
		>
			<svg
				className="block size-full"
				fill="none"
				preserveAspectRatio="none"
				viewBox="0 0 48 48"
			>
				<g id="Search Icon Container">
					<rect
						height="47"
						rx="23.5"
						stroke="var(--stroke-0, #12301B)"
						width="47"
						x="0.5"
						y="0.5"
					/>
					<path
						d="M26 30L19 23.5L26 17"
						id="Icon"
						stroke="var(--stroke-0, #12301B)"
						strokeLinecap="round"
						strokeLinejoin="round"
						strokeWidth="3.2"
					/>
				</g>
			</svg>
		</Link>
	);
}

export default function LoginContent() {
	const router = useRouter();
	const searchParams = useSearchParams();
	const [email, setEmail] = useState("");
	const [password, setPassword] = useState("");
	const [showPassword, setShowPassword] = useState(false);
	const [errors, setErrors] = useState({ email: "", password: "" });
	const [submitting, setSubmitting] = useState(false);
	const [generalError, setGeneralError] = useState("");
	const [showOTP, setShowOTP] = useState(false);
	const [otpError, setOtpError] = useState("");
	const [justLoggedIn, setJustLoggedIn] = useState(false);

	// Add auth-page class to html (and body for backward-compat) when component mounts, remove when unmounts
	useEffect(() => {
		if (typeof document !== "undefined") {
			document.documentElement.classList.add("auth-page");
			document.body.classList.add("auth-page");
		}
		return () => {
			if (typeof document !== "undefined") {
				document.documentElement.classList.remove("auth-page");
				document.body.classList.remove("auth-page");
			}
		};
	}, []);

	// Redirect to auth redirect page after successful login
	useEffect(() => {
		if (justLoggedIn) {
			// Check for action=checkout query parameter and pass it along
			const actionCheckout = searchParams.get("action") === "checkout";
			let redirectUrl = "/auth/redirect";
			if (actionCheckout) {
				redirectUrl = "/auth/redirect?action=checkout";
			}
			// Redirect to the auth redirect page which will handle all redirect logic
			router.push(redirectUrl);
			setJustLoggedIn(false);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [justLoggedIn, router]); // Check searchParams inside the effect

	const validateEmail = (email: string) => {
		const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
		return emailRegex.test(email);
	};

	const handleEmailBlur = () => {
		if (email && !validateEmail(email)) {
			setErrors({ ...errors, email: "Invalid email format" });
		} else {
			setErrors({ ...errors, email: "" });
		}
	};

	interface CognitoError {
		code?: string;
		name?: string;
		message?: string;
	}

	const getErrorMessage = (error: unknown): string => {
		const cognitoError = error as CognitoError;
		const code = cognitoError?.code || cognitoError?.name;
		const message = cognitoError?.message || "";

		// Map common Cognito error codes to user-friendly messages
		switch (code) {
			case "NotAuthorizedException":
				return "Incorrect email or password. Please try again.";
			case "UserNotFoundException":
				return "No account found with this email address.";
			case "UserNotConfirmedException":
				return "Please verify your email address before logging in.";
			case "PasswordResetRequiredException":
				return "Your password must be reset. Please use the forgot password link.";
			case "TooManyFailedAttemptsException":
				return "Too many failed login attempts. Please try again later.";
			case "UserLambdaValidationException":
				return (
					message ||
					"Account validation failed. Please contact support."
				);
			default:
				if (message) {
					// Return the message if it's user-friendly, otherwise use generic
					if (
						message.includes("Incorrect") ||
						message.includes("Invalid")
					) {
						return message;
					}
				}
				return "Login failed. Please check your credentials and try again.";
		}
	};

	const handleSubmit = async (e: React.FormEvent) => {
		e.preventDefault();
		setGeneralError(""); // Clear previous errors

		const newErrors = { email: "", password: "" };

		if (!email) {
			newErrors.email = "Email is required";
		} else if (!validateEmail(email)) {
			newErrors.email = "Invalid email format";
		}

		if (!password) {
			newErrors.password = "Password is required";
		}

		setErrors(newErrors);

		if (newErrors.email || newErrors.password) return;

		try {
			setSubmitting(true);
			setGeneralError(""); // Clear error when starting
			const { loginCognito } = await import("@/services/cognito");
			await loginCognito(email, password);
			toast.success("Logged in successfully");

			// Mark that we just logged in - will wait for user fetch before redirecting
			setJustLoggedIn(true);
		} catch (error: unknown) {
			const cognitoError = error as CognitoError;
			const code = cognitoError?.code || cognitoError?.name;
			// Check if account needs confirmation
			if (code === "UserNotConfirmedException") {
				setOtpError("");
				// Automatically resend OTP when unconfirmed account is detected
				try {
					const { resendSignup } = await import("@/services/cognito");
					await resendSignup({ username: email });
					setShowOTP(true);
					toast.info("Please verify your email to continue", {
						description: `We've sent a 6-digit code to ${email}`,
					});
				} catch {
					// If resend fails, still show OTP component
					setShowOTP(true);
					toast.info("Please verify your email to continue", {
						description: `Enter the 6-digit code sent to ${email}`,
					});
				}
			} else {
				const errorMessage = getErrorMessage(error);
				setGeneralError(errorMessage);
				toast.error(errorMessage);
			}
		} finally {
			setSubmitting(false);
		}
	};

	interface CognitoOTPError {
		code?: string;
		name?: string;
		message?: string;
	}

	const getOTPErrorMessage = (error: unknown): string => {
		const cognitoError = error as CognitoOTPError;
		const code = cognitoError?.code || cognitoError?.name;
		const message = cognitoError?.message || "";

		switch (code) {
			case "CodeMismatchException":
				return "Invalid verification code. Please check the code and try again.";
			case "ExpiredCodeException":
				return "Verification code has expired. Please request a new code.";
			case "NotAuthorizedException":
				return "Invalid verification code. Please check the code and try again.";
			case "LimitExceededException":
				return "Too many attempts. Please try again later.";
			default:
				if (message) {
					if (
						message.includes("invalid") ||
						message.includes("mismatch") ||
						message.includes("incorrect")
					) {
						return "Invalid verification code. Please check the code and try again.";
					}
					if (message.includes("expired")) {
						return "Verification code has expired. Please request a new code.";
					}
				}
				return "Failed to verify code. Please try again.";
		}
	};

	const handleOTPConfirm = async (otp: string) => {
		try {
			setOtpError("");
			const { confirmUser } = await import("@/services/cognito");
			await confirmUser(email, otp);
			toast.success("Account confirmed successfully!");
			// After confirmation, try logging in again
			setShowOTP(false);
			// Auto-login after confirmation
			const { loginCognito } = await import("@/services/cognito");
			await loginCognito(email, password);
			toast.success("Logged in successfully");

			// Mark that we just logged in - will trigger useEffect to handle redirect
			// This ensures checkout intent is properly handled through the same flow
			setJustLoggedIn(true);
		} catch (error: unknown) {
			console.log(error);
			const errorMessage = getOTPErrorMessage(error);
			setOtpError(errorMessage);
			toast.error(errorMessage);
			throw error;
		}
	};

	const handleOTPResend = async () => {
		try {
			setOtpError("");
			const { resendSignup } = await import("@/services/cognito");
			await resendSignup({ username: email });
		} catch (error: unknown) {
			throw error;
		}
	};

	const handleGoogleSignIn = () => {
		import("@/services/cognito").then(({ signInWithProvider }) =>
			signInWithProvider("Google")
		);
	};

	const handleMicrosoftSignIn = () => {
		import("@/services/cognito").then(({ signInWithProvider }) =>
			signInWithProvider("Microsoft")
		);
	};

	// Create data URL for star mask image
	const starMaskImage =
		"data:image/svg+xml,%3Csvg%20preserveAspectRatio%3D%22none%22%20width%3D%22100%25%22%20height%3D%22100%25%22%20overflow%3D%22visible%22%20style%3D%22display%3A%20block%3B%22%20viewBox%3D%220%200%2024%2024%22%20fill%3D%22none%22%20xmlns%3D%22http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%22%3E%0A%3Crect%20id%3D%22Bounding%20box%22%20width%3D%2224%22%20height%3D%2224%22%20fill%3D%22var(--fill-0%2C%20%23D9D9D9)%22%2F%3E%0A%3C%2Fsvg%3E";

	return (
		<div
			className="bg-white relative flex items-stretch min-h-screen rounded-none"
			data-name="Login"
			style={{ borderRadius: 0 }}
		>
			<div
				className="relative basis-0 grow flex flex-col items-center justify-center bg-white overflow-y-auto rounded-none"
				style={{ borderRadius: 0 }}
			>
				<AnimatePresence mode="wait">
					{showOTP ? (
						<motion.div
							key="otp"
							initial={{ opacity: 0, y: 20 }}
							animate={{ opacity: 1, y: 0 }}
							exit={{ opacity: 0, y: -20 }}
							transition={{ duration: 0.3 }}
							className={`w-full max-w-[25rem] px-6 py-8 rounded-none ${styles.loginContainer}`}
							style={{
								borderRadius: 0,
								maxWidth: "25%",
								minWidth: "25rem",
							}}
						>
							<div className="content-stretch flex flex-col gap-[0.5rem] items-start not-italic mb-[2rem]">
								<SearchIconContainer />
							</div>
							<OTPInput
								email={email}
								onSubmit={handleOTPConfirm}
								onResend={handleOTPResend}
								cooldownScope="login"
								title="Verify Your Email"
								description={`Enter the 6-digit code sent to ${email}`}
								errorMessage={otpError}
							/>
						</motion.div>
					) : (
						<motion.div
							key="login"
							initial={{ opacity: 0, y: 20 }}
							animate={{ opacity: 1, y: 0 }}
							exit={{ opacity: 0, y: -20 }}
							transition={{ duration: 0.5 }}
							className={`w-full max-w-[25rem] px-6 py-8 rounded-none ${styles.loginContainer}`}
							style={{
								borderRadius: 0,
								maxWidth: "25%",
								minWidth: "25rem",
							}}
						>
							{/* Header */}
							<div className="content-stretch flex flex-col gap-[0.5rem] items-start not-italic mb-[2rem]">
								<SearchIconContainer />
								<p
									className={`font-['Inter:Semi_Bold',sans-serif] font-semibold text-[#101828] ${styles.loginTitle}`}
									style={{ marginTop: "2.5rem" }}
								>
									Login
								</p>
								<p
									className={`font-['Inter:Regular',sans-serif] font-normal text-[#475467] ${styles.loginSubtitle}`}
								>
									Login to access your account
								</p>
							</div>

							{/* Social Sign In Buttons */}
							<div className="content-stretch flex flex-col gap-[0.75rem] items-center justify-center mb-[1.5rem]">
								<motion.button
									whileHover={{ scale: 1.02 }}
									whileTap={{ scale: 0.98 }}
									onClick={handleGoogleSignIn}
									data-testid="login-google-button"
									className="bg-white relative rounded-[0.75rem] w-full"
								>
									<div className="flex flex-row items-center justify-center overflow-clip rounded-[inherit] size-full">
										<div className="box-border content-stretch flex gap-[0.75rem] items-center justify-center px-[1rem] py-[0.625rem] w-full">
											<div className="overflow-clip relative shrink-0 size-[1.5rem]">
												<div className="relative w-full h-full">
													<div className="relative w-full h-full">
														<div className="absolute inset-[40.99%_0.97%_12.07%_51%]">
															<svg
																className="block size-full"
																fill="none"
																preserveAspectRatio="none"
																viewBox="0 0 12 12"
															>
																<path
																	d={
																		svgPaths.p29993c00
																	}
																	fill="var(--fill-0, #4285F4)"
																/>
															</svg>
														</div>
														<div className="absolute bottom-0 left-[6.32%] right-[15.86%] top-[59.59%]">
															<svg
																className="block size-full"
																fill="none"
																preserveAspectRatio="none"
																viewBox="0 0 19 10"
															>
																<path
																	d={
																		svgPaths.p3e923d00
																	}
																	fill="var(--fill-0, #34A853)"
																/>
															</svg>
														</div>
														<div className="absolute inset-[27.56%_77.07%_27.54%_1%]">
															<svg
																className="block size-full"
																fill="none"
																preserveAspectRatio="none"
																viewBox="0 0 6 11"
															>
																<path
																	d={
																		svgPaths.p18a6ed00
																	}
																	fill="var(--fill-0, #FBBC04)"
																/>
															</svg>
														</div>
														<div className="absolute bottom-[59.56%] left-[6.32%] right-[15.54%] top-0">
															<svg
																className="block size-full"
																fill="none"
																preserveAspectRatio="none"
																viewBox="0 0 19 10"
															>
																<path
																	d={
																		svgPaths.p2d291200
																	}
																	fill="var(--fill-0, #EA4335)"
																/>
															</svg>
														</div>
													</div>
												</div>
											</div>
											<p
												className={`font-['Inter:Semi_Bold',sans-serif] font-semibold not-italic text-[#12301b] text-nowrap whitespace-pre ${styles.loginButtonText}`}
											>
												Sign in with Google
											</p>
											<div
												aria-hidden="true"
												className={`absolute border ${
													errors.email
														? "border-red-500"
														: "border-[#dde3e9]"
												} border-solid inset-0 pointer-events-none rounded-[0.75rem] shadow-[0px_1px_2px_0px_rgba(16,24,40,0.05)]`}
											/>
										</div>
									</div>
								</motion.button>

								<motion.button
									whileHover={{ scale: 1.02 }}
									whileTap={{ scale: 0.98 }}
									onClick={handleMicrosoftSignIn}
									data-testid="login-microsoft-button"
									className="bg-white relative rounded-[0.75rem] w-full"
								>
									<div className="flex flex-row items-center justify-center overflow-clip rounded-[inherit] size-full">
										<div className="box-border content-stretch flex gap-[0.75rem] items-center justify-center px-[1rem] py-[0.625rem] w-full">
											<div className="overflow-clip relative shrink-0 size-[1.5rem]">
												<svg
													className="block size-full"
													fill="none"
													preserveAspectRatio="none"
													viewBox="0 0 24 24"
												>
													<g id="Group">
														<path
															d={
																svgPaths.p39c85400
															}
															fill="var(--fill-0, #F1511B)"
														/>
														<path
															d={
																svgPaths.p14f3a7f0
															}
															fill="var(--fill-0, #80CC28)"
														/>
														<path
															d={
																svgPaths.p1cf9b200
															}
															fill="var(--fill-0, #00ADEF)"
														/>
														<path
															d={
																svgPaths.p3dbbfc80
															}
															fill="var(--fill-0, #FBBC09)"
														/>
													</g>
												</svg>
											</div>
											<p
												className={`font-['Inter:Semi_Bold',sans-serif] font-semibold not-italic text-[#12301b] text-nowrap whitespace-pre ${styles.loginButtonText}`}
											>
												Sign in with Microsoft
											</p>
											<div
												aria-hidden="true"
												className={`absolute border ${
													errors.email
														? "border-red-500"
														: "border-[#dde3e9]"
												} border-solid inset-0 pointer-events-none rounded-[0.75rem] shadow-[0px_1px_2px_0px_rgba(16,24,40,0.05)]`}
											/>
										</div>
									</div>
								</motion.button>
							</div>

							{/* Divider */}
							<div className="content-stretch flex gap-[1rem] items-center mb-[1.5rem]">
								<p
									className={`font-['Inter:Bold',sans-serif] font-bold not-italic text-[#12301b] text-nowrap uppercase whitespace-pre ${styles.loginDividerText}`}
								>
									Login with email
								</p>
								<div className="basis-0 bg-[#d9d9d9] grow h-px min-h-px min-w-px shrink-0" />
							</div>

							{/* Form */}
							<form
								onSubmit={handleSubmit}
								className="content-stretch flex flex-col gap-[1.25rem] mb-[1rem]"
							>
								{/* Email Field */}
								<div className="content-stretch flex flex-col gap-[0.375rem] items-start w-full">
									<p
										className={`font-['Inter:Medium',sans-serif] font-medium not-italic text-[#12301b] text-nowrap whitespace-pre ${styles.loginLabelText}`}
									>
										Email*
									</p>
									<motion.div
										className="bg-white relative rounded-[0.75rem] w-full"
										whileFocus={{ scale: 1.01 }}
									>
										<div className="flex flex-row items-center overflow-clip rounded-[inherit] size-full">
											<div className="box-border content-stretch flex gap-[0.5rem] items-center px-[0.875rem] py-[0.625rem] w-full">
												<input
													type="email"
													value={email}
													data-testid="login-email-input"
													onChange={(e) =>
														setEmail(e.target.value)
													}
													onBlur={handleEmailBlur}
													placeholder="Enter your email"
													className={`basis-0 font-['Inter:Regular',sans-serif] font-normal grow min-h-px min-w-px not-italic text-[#12301b] outline-none bg-transparent ${styles.loginInputText}`}
												/>
											</div>
										</div>
										<div
											aria-hidden="true"
											className={`absolute border ${
												errors.email
													? "border-red-500"
													: "border-[#dde3e9]"
											} border-solid inset-0 pointer-events-none rounded-[0.75rem] shadow-[0px_1px_2px_0px_rgba(16,24,40,0.05)]`}
										/>
									</motion.div>
									{errors.email && (
										<motion.p
											initial={{ opacity: 0, y: -5 }}
											animate={{ opacity: 1, y: 0 }}
											data-testid="login-email-error"
											className={`font-['Inter:Regular',sans-serif] text-red-500 ${styles.loginLabelText}`}
										>
											{errors.email}
										</motion.p>
									)}
								</div>

								{/* Password Field */}
								<div className="content-stretch flex flex-col gap-[0.375rem] items-start w-full">
									<div className="content-stretch flex gap-[0.375rem] items-start not-italic w-full">
										<p
											className={`basis-0 font-['Inter:Medium',sans-serif] font-medium grow min-h-px min-w-px text-[#12301b] ${styles.loginLabelText}`}
										>
											Password*
										</p>
										<Link
											href="/forgot-password"
											data-testid="login-forgot-password-link"
											className={`font-['Inter:Semi_Bold',sans-serif] font-semibold text-[#2769af] text-nowrap whitespace-pre hover:opacity-70 transition-opacity ${styles.loginLinkText}`}
										>
											Forgot Password?
										</Link>
									</div>
									<motion.div
										className="bg-white relative rounded-[0.75rem] w-full"
										whileFocus={{ scale: 1.01 }}
									>
										<div className="flex flex-row items-center overflow-clip rounded-[inherit] size-full">
											<div className="box-border content-stretch flex gap-[0.5rem] items-center px-[0.875rem] py-[0.625rem] w-full">
												<input
													type={
														showPassword
															? "text"
															: "password"
													}
													value={password}
													data-testid="login-password-input"
													onChange={(e) =>
														setPassword(
															e.target.value
														)
													}
													onKeyDown={(e) => {
														if (
															e.key === "Enter" &&
															!submitting
														) {
															e.preventDefault();
															const form =
																e.currentTarget.closest(
																	"form"
																);
															if (form) {
																form.requestSubmit();
															}
														}
													}}
													placeholder="Enter your password"
													className={`basis-0 font-['Inter:Regular',sans-serif] font-normal grow min-h-px min-w-px not-italic text-[#12301b] outline-none bg-transparent ${styles.loginInputText}`}
												/>
												<motion.button
													type="button"
													whileHover={{ scale: 1.1 }}
													whileTap={{ scale: 0.9 }}
													onClick={() =>
														setShowPassword(
															!showPassword
														)
													}
													className="shrink-0"
												>
													{showPassword ? (
														<EyeOff className="w-5 h-5 text-[#475467]" />
													) : (
														<Eye className="w-5 h-5 text-[#475467]" />
													)}
												</motion.button>
											</div>
										</div>
										<div
											aria-hidden="true"
											className={`absolute border ${
												errors.password
													? "border-red-500"
													: "border-[#dde3e9]"
											} border-solid inset-0 pointer-events-none rounded-[0.75rem] shadow-[0px_1px_2px_0px_rgba(16,24,40,0.05)]`}
										/>
									</motion.div>
									{errors.password && (
										<motion.p
											initial={{ opacity: 0, y: -5 }}
											animate={{ opacity: 1, y: 0 }}
											data-testid="login-password-error"
											className={`font-['Inter:Regular',sans-serif] text-red-500 ${styles.loginLabelText}`}
										>
											{errors.password}
										</motion.p>
									)}
								</div>
							</form>

							{/* General Error Message */}
							{generalError && (
								<motion.div
									initial={{ opacity: 0, y: -10 }}
									animate={{ opacity: 1, y: 0 }}
									exit={{ opacity: 0, y: -10 }}
									className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg"
								>
									<p className="text-sm text-red-600 font-medium">
										{generalError}
									</p>
								</motion.div>
							)}

							{/* Login Button */}
							<motion.button
								type="submit"
								onClick={handleSubmit}
								disabled={submitting}
								data-testid="login-submit-button"
								whileHover={submitting ? {} : { scale: 1.02 }}
								whileTap={submitting ? {} : { scale: 0.98 }}
								className={`bg-[#cbf7a6] h-[3.25rem] relative rounded-[2.25rem] w-full mb-[2rem] ${
									submitting
										? "opacity-70 cursor-not-allowed"
										: ""
								}`}
							>
								<div
									aria-hidden="true"
									className="absolute border border-[#12301b] border-solid inset-0 pointer-events-none rounded-[2.25rem]"
								/>
								<div className="flex flex-row items-center justify-center size-full">
									<div className="box-border content-stretch flex gap-[0.5rem] h-[3.25rem] items-center justify-center px-[1.5rem] py-[1rem] w-full">
										{submitting ? (
											<>
												<Loader2 className="w-5 h-5 text-[#12301b] animate-spin" />
												<p
													className={`font-['Inter:Extra_Bold',sans-serif] font-extrabold not-italic text-[#12301b] text-nowrap tracking-[0.01rem] whitespace-pre ${styles.loginButtonText}`}
												>
													Logging in...
												</p>
											</>
										) : (
											<>
												<div className="overflow-clip relative shrink-0 size-[1.5rem] flex items-center justify-center">
													<div className="relative w-[75%] h-[75%]">
														<div
															className="relative w-full h-full"
															style={
																{
																	"--fill-0":
																		"rgba(18, 48, 27, 1)",
																	"--stroke-0":
																		"rgba(18, 48, 27, 1)",
																} as React.CSSProperties
															}
														>
															<svg
																className="block size-full"
																fill="none"
																preserveAspectRatio="none"
																viewBox="0 0 20 20"
															>
																<path
																	d={
																		svgPaths.p3bdb3200
																	}
																	stroke="var(--stroke-0, black)"
																	strokeLinecap="round"
																	strokeLinejoin="round"
																	strokeWidth="2"
																/>
															</svg>
														</div>
													</div>
												</div>
												<p
													className={`font-['Inter:Extra_Bold',sans-serif] font-extrabold not-italic text-[#12301b] text-nowrap tracking-[0.01rem] whitespace-pre ${styles.loginButtonText}`}
												>
													Login
												</p>
												<div className="overflow-clip relative shrink-0 size-[1.5rem] flex items-center justify-center">
													<div className="relative w-[75%] h-[75%]">
														<div
															className="relative w-full h-full"
															style={
																{
																	"--fill-0":
																		"rgba(18, 48, 27, 1)",
																	"--stroke-0":
																		"rgba(18, 48, 27, 1)",
																} as React.CSSProperties
															}
														>
															<svg
																className="block size-full"
																fill="none"
																preserveAspectRatio="none"
																viewBox="0 0 20 20"
															>
																<path
																	d={
																		svgPaths.p3bdb3200
																	}
																	stroke="var(--stroke-0, black)"
																	strokeLinecap="round"
																	strokeLinejoin="round"
																	strokeWidth="2"
																/>
															</svg>
														</div>
													</div>
												</div>
											</>
										)}
									</div>
								</div>
							</motion.button>

							{/* Sign Up Section */}
							<div className="bg-[#edffde] box-border content-stretch flex flex-col gap-[0.25rem] items-center p-[1rem] rounded-[0.75rem]">
								<div className="content-stretch flex gap-[0.25rem] items-start justify-center w-full">
									<p
										className={`font-['Inter:Regular',sans-serif] font-normal not-italic text-[#475467] text-nowrap whitespace-pre ${styles.loginLinkText}`}
									>
										New to the platform?
									</p>
									<Link
										href="/signup"
										data-testid="login-signup-link"
										className="content-stretch flex gap-[0.5rem] items-center justify-center overflow-clip shrink-0"
									>
										<p
											className={`font-['Inter:Semi_Bold',sans-serif] font-semibold not-italic text-[#2769af] text-nowrap whitespace-pre hover:opacity-70 transition-opacity ${styles.loginLinkText}`}
										>
											Sign Up
										</p>
									</Link>
								</div>
								<div className="content-stretch flex gap-[0.25rem] items-center shrink-0">
									<div className="relative shrink-0 size-[1.5rem] flex items-center justify-center">
										<div
											className="relative w-[81.25%] h-[71.92%] mask-alpha mask-intersect mask-no-clip mask-no-repeat mask-position-[-0.10625rem_-0.16875rem] mask-size-[1.5rem_1.5rem]"
											style={{
												maskImage: `url('${starMaskImage}')`,
											}}
										>
											<svg
												className="block size-full"
												fill="none"
												preserveAspectRatio="none"
												viewBox="0 0 21 18"
											>
												<path
													d={svgPaths.p221c8200}
													fill="var(--fill-0, #417714)"
												/>
											</svg>
										</div>
									</div>
									<p
										className={`font-['Inter:Semi_Bold',sans-serif] font-semibold not-italic text-[#417714] text-nowrap tracking-[0.0075rem] whitespace-pre ${styles.loginPromoText}`}
									>
										{`Try Free for 14 days • No Credit Card Required `}
									</p>
									<div className="relative shrink-0 size-[1.5rem] flex items-center justify-center">
										<div
											className="relative w-[81.25%] h-[71.92%] mask-alpha mask-intersect mask-no-clip mask-no-repeat mask-position-[-0.10625rem_-0.16875rem] mask-size-[1.5rem_1.5rem]"
											style={{
												maskImage: `url('${starMaskImage}')`,
											}}
										>
											<svg
												className="block size-full"
												fill="none"
												preserveAspectRatio="none"
												viewBox="0 0 21 18"
											>
												<path
													d={svgPaths.p221c8200}
													fill="var(--fill-0, #417714)"
												/>
											</svg>
										</div>
									</div>
								</div>
							</div>

							{/* Terms and Privacy */}
							<motion.p
								initial={{ opacity: 0 }}
								animate={{ opacity: 1 }}
								transition={{ delay: 0.3 }}
								className={`font-['Inter:Regular',sans-serif] font-normal not-italic text-[#697b8e] text-center mt-[2rem] ${styles.loginTermsText}`}
							>
								<Link
									href="/terms-of-service"
									data-testid="login-terms-link"
									className="[text-decoration-skip-ink:none] [text-underline-position:from-font] decoration-solid underline inline-block hover:opacity-70 transition-opacity"
								>
									Terms and Condition
								</Link>
								<span>{` and `}</span>
								<Link
									href="/privacy-policy"
									data-testid="login-privacy-link"
									className="[text-decoration-skip-ink:none] [text-underline-position:from-font] decoration-solid underline inline-block hover:opacity-70 transition-opacity"
								>
									Privacy policy
								</Link>
							</motion.p>
						</motion.div>
					)}
				</AnimatePresence>
			</div>
		</div>
	);
}
